import { Navigate, useRoutes } from 'react-router-dom'

import { useAuth } from '@/providers'

import { AdminPanelLayout, AuthLayout, BoardLayout } from '@/layouts'
import {
  AiCenter,
  AnalyticsDashboard,
  AnalyticsOrderTracker,
  AnalyticsTotalEconomics,
  AuthSignIn,
  BusinessList,
  CatalogProcessorList,
  GeneratePurchaseOrder,
  InventoryDetail,
  LogisticListPage,
  OrderCenterPage,
  OrderCenterPageShow,
  ProductList,
  ProductShow,
  ProfileList,
  ProfileShowV2,
  QuotationRequestShow,
  QuotationRequestV2List,
  QuotationRequestV2Show,
  QuotationV2Form,
  QuotationV2List,
  QuotationV2Show,
  SupplierOrderForm,
  SupplierOrderManagementPage,
  SupplyOrderDetail,
  SupplySupplierList,
  SupplySupplierShow,
  SupplyTransportCompaniesShow,
  TabSupplierProducts,
  LeadIntake
} from '@/pages'

import { SaleBoard } from '@/pages/board/sale-board'
import { QuotationRequestCreate } from '@/pages/business/quotation-request-v2/create'
import { ROUTES_ADMIN, ROUTES_AUTH } from './router-routes'

const routes = (isSigned: boolean) => [
  {
    path: '/',
    element: <Navigate to={ROUTES_ADMIN.BUSINESS.RFQ.fullPath()} />,
  },
  {
    path: ROUTES_AUTH.path,
    element: <AuthLayout />,
    children: [
      {
        element: <AuthSignIn />,
        path: ROUTES_AUTH.SIGN_IN.path,
      },
    ],
  },
  {
    path: ROUTES_ADMIN.path,
    element: <BoardLayout />,
    children: [
      {
        path: ROUTES_ADMIN.BOARD.SALE.path,
        element: <SaleBoard />,
      },
    ],
  },
  {
    path: ROUTES_ADMIN.path,
    element: isSigned ? (
      <AdminPanelLayout />
    ) : (
      <Navigate to={ROUTES_AUTH.SIGN_IN.fullPath()} />
    ),
    children: [
      {
        element: <ProductList />,
        path: ROUTES_ADMIN.CATALOG.PRODUCT.path,
      },
      {
        element: <ProductShow />,
        path: ROUTES_ADMIN.CATALOG.PRODUCT.SHOW.path,
      },
      {
        element: <BusinessList />,
        path: ROUTES_ADMIN.BUSINESS.path,
      },
      {
        element: <BusinessList />,
        path: ROUTES_ADMIN.BUSINESS.RFQ_OLD.path,
      },
      {
        element: <QuotationRequestShow />,
        path: `${ROUTES_ADMIN.BUSINESS.RFQ_OLD.SHOW.path}`,
      },
      {
        element: <QuotationRequestV2List />,
        path: `${ROUTES_ADMIN.BUSINESS.RFQ.path}`,
      },
      {
        element: <QuotationRequestV2Show />,
        path: `${ROUTES_ADMIN.BUSINESS.RFQ.SHOW.path}`,
      },
      {
        element: <QuotationRequestCreate />,
        path: `${ROUTES_ADMIN.BUSINESS.RFQ.CREATE.path}`,
      },
      {
        element: <QuotationV2List />,
        path: `${ROUTES_ADMIN.BUSINESS.QUOTATION.path}`,
      },
      {
        element: <QuotationV2Show />,
        path: `${ROUTES_ADMIN.BUSINESS.QUOTATION.SHOW.path}`,
      },
      {
        element: <QuotationV2Form />,
        path: `${ROUTES_ADMIN.BUSINESS.QUOTATION.CREATE.path}`,
      },
      {
        element: <QuotationV2Form />,
        path: `${ROUTES_ADMIN.BUSINESS.QUOTATION.UPDATE.path}`,
      },
      {
        element: <QuotationV2Show />,
        path: `${ROUTES_ADMIN.BUSINESS.QUOTATION.SHOW.path}`,
      },
      {
        element: <ProfileList />,
        path: ROUTES_ADMIN.PROFILE.path,
      },
      {
        element: <ProfileShowV2 />,
        path: `${ROUTES_ADMIN.PROFILE.SHOW.path}`,
      },
      {
        element: <ProfileShowV2 />,
        path: `${ROUTES_ADMIN.PROFILE.SHOW_V2.path}`,
      },
      {
        element: <SupplySupplierList />,
        path: `${ROUTES_ADMIN.SUPPLY.SUPPLIERS.path}`,
      },
      {
        element: <SupplySupplierList />,
        path: `${ROUTES_ADMIN.SUPPLY.SUPPLIERS.SUPPLIERS.path}`,
      },
      {
        element: <SupplySupplierShow />,
        path: `${ROUTES_ADMIN.SUPPLY.SUPPLIERS.SUPPLIERS.SHOW.path}`,
      },
      {
        element: <TabSupplierProducts />,
        path: `${ROUTES_ADMIN.SUPPLY.SUPPLIERS.SUPPLIER_FORECASTS.path}`,
      },
      {
        element: <LogisticListPage />,
        path: `${ROUTES_ADMIN.SUPPLY.ADMIN_LOGISTIC.path}`,
      },
      {
        element: <SupplyTransportCompaniesShow />,
        path: `${ROUTES_ADMIN.SUPPLY.ADMIN_LOGISTIC.SHOW.path}`,
      },
      {
        element: <SupplierOrderManagementPage />,
        path: `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.path}`,
      },
      {
        element: <SupplyOrderDetail />,
        path: `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.SHOW.path}`,
      },
      {
        element: <GeneratePurchaseOrder />,
        path: `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.GENERATE_PURCHASE_ORDER.CREATE.path}`,
      },
      {
        element: <GeneratePurchaseOrder />,
        path: `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.GENERATE_PURCHASE_ORDER.UPDATE.path}`,
      },
      {
        element: <InventoryDetail />,
        path: `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.INVENTORY.SHOW.path}`,
      },
      {
        element: <SupplierOrderForm />,
        path: `${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.UPDATE.path}`,
      },
      {
        element: <AnalyticsDashboard />,
        path: `${ROUTES_ADMIN.ANALYTICS.DASHBOARD.path}`,
      },
      {
        element: <AnalyticsOrderTracker />,
        path: `${ROUTES_ADMIN.ANALYTICS.ORDER_TRACKER.path}`,
      },
      {
        element: <AnalyticsTotalEconomics />,
        path: `${ROUTES_ADMIN.ANALYTICS.TOTAL_ECONOMICS.path}`,
      },
      {
        element: <CatalogProcessorList />,
        path: `${ROUTES_ADMIN.CATALOG.PROCESSOR.path}`,
      },
      {
        element: <OrderCenterPage />,
        path: `${ROUTES_ADMIN.ORDER_CENTER.path}`,
      },
      {
        element: <OrderCenterPage />,
        path: `${ROUTES_ADMIN.ORDER_CENTER.path}`,
      },
      {
        element: <OrderCenterPageShow />,
        path: `${ROUTES_ADMIN.ORDER_CENTER.ALL.SHOW.path}`,
      },
      {
        element: <AiCenter />,
        path: `${ROUTES_ADMIN.AI_CENTER.path}`,
      },
      {
        element: <LeadIntake />,
        path: `${ROUTES_ADMIN.LEAD_INTAKE.path}`,
      }
    ],
  },
]

export const Routes = () => {
  const { isSigned } = useAuth()

  const routing = useRoutes(routes(isSigned))

  return routing
}
